import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useNavigate } from '@common/hooks';
import { ErrorHandler, PrimaryButton } from '@common/components';
import { createMessage } from 'works/legacy/lib/message-helper';
import { ResubmitApplication } from '@applicant-flow/graphql/mutations';
import { StaffingRequestSubmissionDrawer } from '@jobs/pages';
import { CreateWorksSubmittalPacket } from 'works/legacy/graphql/works-submittal-packet';

export function StaffingRequestResubmitButton({
  children,
  staffingRequestId,
  staffingRequestType,
  applicationId,
  selectedWorksProfile,
}) {
  const [submissionFormVisible, setSubmissionFormVisible] = useState(false);
  const navigate = useNavigate();

  const [createWorkSubmittalPacket, { error: createSubmittalPacketError }] =
    useMutation(CreateWorksSubmittalPacket);

  const [resubmitApplication, { error: resubmitApplicationError }] =
    useMutation(ResubmitApplication, {
      onCompleted: (data) => {
        const worksProfile =
          data.resubmitApplication.staffingRequestApplication.worksProfile;
        const candidateName = worksProfile.fullName;
        createMessage('success', `${candidateName} has been resubmitted!`);
        navigate(
          `/works/supplier/staffing-request/${staffingRequestId}/submitted`
        );
      },
      refetchQueries: ['SupplierJobPageQuery'],
    });

  const anyError = createSubmittalPacketError || resubmitApplicationError;
  if (anyError) {
    return (
      <ErrorHandler
        error={anyError}
        messageType="contextual"
        log="StaffingRequestResubmitButton"
      />
    );
  }

  const getSubmittalPacketId = async ({ submittalPacket }) => {
    const submittalPacketId = submittalPacket?.target?.value;

    if (submittalPacketId) return submittalPacketId;

    const { data } = await createWorkSubmittalPacket({
      variables: {
        worksProfileId: selectedWorksProfile.id,
        file: submittalPacket.file,
      },
    });

    return data?.createWorksSubmittalPacket?.worksSubmittalPacket?.id;
  };

  const handleSubmit = async (values) => {
    const submittalPacketId = await getSubmittalPacketId(values);

    resubmitApplication({
      variables: {
        staffingRequestApplicationId: applicationId,
        worksSubmittalPacketId: submittalPacketId,
        input: {
          interviewAvailability: values.interviewAvailability,
          availableToStartAt: values.availableToStartAt,
          timeoffDescription: values.timeoffDescription,
        },
      },
    });
  };

  return (
    <React.Fragment>
      <StaffingRequestSubmissionDrawer
        onSubmit={handleSubmit}
        visible={submissionFormVisible}
        onClose={() => setSubmissionFormVisible(false)}
        selectedWorksProfile={selectedWorksProfile}
        staffingRequestType={staffingRequestType}
      />
      <PrimaryButton onClick={() => setSubmissionFormVisible(true)}>
        {children}
      </PrimaryButton>
    </React.Fragment>
  );
}

StaffingRequestResubmitButton.propTypes = {
  staffingRequestType: PropTypes.oneOf(['long_term', 'on_demand']),
  staffingRequestId: PropTypes.string.isRequired,
  selectedWorksProfile: PropTypes.shape({
    id: PropTypes.string.isRequired,
    fullName: PropTypes.string.isRequired,
  }).isRequired,
  applicationId: PropTypes.string.isRequired,
  children: PropTypes.string.isRequired,
};

StaffingRequestResubmitButton.defaultProps = {
  children: 'Submit Candidate',
};
